import { FileText } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { MentionTextarea } from './MentionTextarea';
import { splitBody, toBodyText, toEditableText } from './mentions';
import type { MentionCandidate, MentionPerson, ThreadLabels, ThreadMessage } from './types';
import { cn } from './utils';

interface ThreadMessagesProps {
	messages: ThreadMessage[];
	labels: ThreadLabels;
	/** The menu beside a message — the app's own dropdown, driven by canEdit / canDelete. */
	messageActions?: (message: ThreadMessage) => ReactNode;
	/** Opens a message's attachment in the app's file viewer. */
	onOpenAttachment?: (message: ThreadMessage, index: number) => void;
	/** The message being edited in place, if any. */
	editingId?: string | null;
	/** People who can be tagged — for the inline editor's @-picker. */
	candidates?: MentionCandidate[];
	onEditSave?: (id: string, body: string) => void;
	onEditCancel?: () => void;
	editSaving?: boolean;
	emptyState?: ReactNode;
}

/**
 * A thread's messages, oldest first. Your own sit on the right. Tags render as
 * chips from `mentions` — a raw `<@id>` token is never shown. Deleted messages
 * keep their place and say so. Presentational: the app fetches, posts and owns
 * every menu.
 */
export function ThreadMessages({
	messages,
	labels,
	messageActions,
	onOpenAttachment,
	editingId,
	candidates = [],
	onEditSave,
	onEditCancel,
	editSaving,
	emptyState,
}: ThreadMessagesProps) {
	if (messages.length === 0) {
		return (
			<div className="px-4 py-10 text-center text-sm text-text-4">
				{emptyState ?? 'No messages yet.'}
			</div>
		);
	}

	return (
		<ol className="flex flex-col gap-4">
			{messages.map((m) => (
				<li
					key={m.id}
					className={cn('flex flex-col', m.isMine ? 'items-end' : 'items-start')}
				>
					<div
						className={cn(
							// Same reading order on both sides — only the alignment moves.
							'mb-1 flex items-baseline gap-1.5 text-xs text-text-4',
							m.isMine && 'justify-end'
						)}
					>
						<span className="font-medium text-text-2">{m.author.name}</span>
						{labels.sideLabel && <span>{labels.sideLabel(m.author.side)}</span>}
						{m.createdAt && <span>· {labels.formatTime(m.createdAt)}</span>}
						{m.editedAt && !m.deletedAt && <span>· edited</span>}
					</div>

					<div
						className={cn(
							'flex max-w-[85%] items-start gap-1',
							m.isMine && 'flex-row-reverse'
						)}
					>
						{m.deletedAt ? (
							<div className="rounded-[var(--r-3)] border border-dashed border-line-2 px-3 py-2 text-sm text-text-4 italic">
								Message deleted
							</div>
						) : editingId === m.id ? (
							<InlineEditor
								message={m}
								candidates={candidates}
								saving={!!editSaving}
								onSave={(body) => onEditSave?.(m.id, body)}
								onCancel={() => onEditCancel?.()}
							/>
						) : (
							<div
								className={cn(
									'min-w-0 rounded-[var(--r-3)] px-3 py-2',
									m.isMine
										? 'bg-accent-soft text-text-1'
										: 'border border-line-1 bg-surface text-text-1'
								)}
							>
								{m.body && <MessageBody body={m.body} mentions={m.mentions} />}
								{m.attachments.length > 0 && (
									<div className={cn('flex flex-wrap gap-1.5', m.body && 'mt-2')}>
										{m.attachments.map((a, i) => (
											<AttachmentChip
												key={`${a.name}-${i}`}
												name={a.name}
												mimeType={a.mimeType}
												url={a.url}
												onOpen={
													onOpenAttachment && a.url
														? () => onOpenAttachment(m, i)
														: undefined
												}
											/>
										))}
									</div>
								)}
							</div>
						)}
						{!m.deletedAt && editingId !== m.id && messageActions?.(m)}
					</div>
				</li>
			))}
		</ol>
	);
}

function MessageBody({ body, mentions }: { body: string; mentions: MentionPerson[] }) {
	return (
		<p className="text-base break-words whitespace-pre-wrap">
			{splitBody(body, mentions).map((part, i) =>
				part.kind === 'text' ? (
					<span key={i}>{part.text}</span>
				) : (
					<span
						key={i}
						className="rounded-[var(--r-1)] bg-surface-fill px-1 font-medium text-text-1"
					>
						@{part.name}
					</span>
				)
			)}
		</p>
	);
}

function AttachmentChip({
	name,
	mimeType,
	url,
	onOpen,
}: {
	name: string;
	mimeType: string;
	url?: string | null;
	onOpen?: () => void;
}) {
	const isImage = mimeType.startsWith('image/') && !!url;
	return (
		<button
			type="button"
			onClick={onOpen}
			disabled={!onOpen}
			className="flex max-w-56 cursor-pointer items-center gap-2 rounded-[var(--r-2)] border border-line-1 bg-surface px-2 py-1.5 text-left transition-colors hover:bg-surface-hover disabled:cursor-default"
		>
			{isImage ? (
				<img
					src={url!}
					alt=""
					className="size-8 shrink-0 rounded-[var(--r-1)] object-cover"
				/>
			) : (
				<FileText size={16} className="shrink-0 text-text-3" />
			)}
			<span className="truncate text-sm text-text-2">{name}</span>
		</button>
	);
}

function InlineEditor({
	message,
	candidates,
	saving,
	onSave,
	onCancel,
}: {
	message: ThreadMessage;
	candidates: MentionCandidate[];
	saving: boolean;
	onSave: (body: string) => void;
	onCancel: () => void;
}) {
	const [text, setText] = useState(() => toEditableText(message.body, message.mentions));
	const [picked, setPicked] = useState<MentionPerson[]>(message.mentions);
	const body = toBodyText(text, picked);
	// Text only — an edit can't empty a message that has no files to stand on.
	const canSave = !saving && (body.length > 0 || message.attachments.length > 0);

	return (
		<div className="flex w-[min(28rem,80vw)] flex-col gap-1.5">
			<MentionTextarea
				value={text}
				onChange={setText}
				candidates={candidates}
				onMention={(p) => setPicked((prev) => [...prev, p])}
				onSubmit={() => canSave && onSave(body)}
				disabled={saving}
				rows={3}
			/>
			<div className="flex justify-end gap-1.5">
				<button
					type="button"
					onClick={onCancel}
					disabled={saving}
					className="cursor-pointer rounded-md px-2 py-1 text-sm text-text-3 hover:text-text-1"
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={() => onSave(body)}
					disabled={!canSave}
					className="cursor-pointer rounded-md bg-primary px-2.5 py-1 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
				>
					{saving ? 'Saving…' : 'Save'}
				</button>
			</div>
		</div>
	);
}
